import React, { useState } from 'react';
import { ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { LoaderSpinner } from '../LoaderSpinner';

const CSVExportButton = ({
  data = [],
  columns = [],
  filename = "report",
  onExport = null,
  disabled = false,
  className = "",
  labelClassName = "",
  variant = "primary", // primary, secondary, outline
  iconOnlyOnMobile = false
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Generate timestamp for filename
  const generateTimestamp = () => {
    const now = new Date();
    return now.toISOString().split('T')[0].replace(/-/g, '') + '_' + 
           now.toTimeString().split(' ')[0].replace(/:/g, '');
  };

  // Convert data to CSV format
  const convertToCSV = (data, columns) => {
    // Create header row
    const headers = columns.map(col => col.label || col.key || col).join(',');
    
    // Create data rows
    const rows = data.map(row => {
      return columns.map(col => {
        const key = col.key || col.accessor || col;
        let value = row?.[key] ?? '';
        
        // Handle different data types
        if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value);
        }
        
        // Escape commas and quotes in CSV
        if (typeof value === 'string') {
          value = value.replace(/"/g, '""'); // Escape quotes
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }
        }
        
        return value;
      }).join(',');
    });

    return [headers, ...rows].join('\n');
  };

  // Download CSV file
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Fallback for older browsers
      throw new Error('CSV export not supported in this browser');
    }
  };

  // Handle export process
  const handleExport = async () => {
    if (disabled || isExporting) return;

    setIsExporting(true);
    setExportError(null);

    try {
      let exportData = data;
      let exportColumns = columns;

      // If custom export function provided, use it
      if (onExport && typeof onExport === 'function') {
        const result = await onExport();
        
        // If onExport handled the entire export process (server-side export)
        if (result && (result.success || result.handled)) {
          // Export was handled by onExport function, nothing more to do
          return;
        }
        
        // If onExport returned data for client-side processing
        if (result && (result.data || result.columns)) {
          exportData = result.data || exportData;
          exportColumns = result.columns || exportColumns;
        }
      }

      // Validate data before conversion (only needed for client-side export)
      if (!exportData || exportData.length === 0) {
        throw new Error('No data available to export. Please ensure the report has loaded data before attempting to export.');
      }

      if (!exportColumns || exportColumns.length === 0) {
        throw new Error('No columns defined for export. Please check the report configuration.');
      }

      // Convert to CSV
      const csvContent = convertToCSV(exportData, exportColumns);
      
      // Generate filename with timestamp
      const timestamp = generateTimestamp();
      const csvFilename = `${filename}_${timestamp}.csv`;
      
      // Download file
      downloadCSV(csvContent, csvFilename);
      
    } catch (error) {
      console.error('Export error:', error);
      setExportError(error.message || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  // Button styles based on variant
  const getButtonStyles = () => {
    const baseStyles = "inline-flex relative items-center px-3 sm:px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200";
    
    switch (variant) {
      case 'secondary':
        return `${baseStyles} text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 focus:ring-gray-500`;
      case 'outline':
        return `${baseStyles} text-gray-900 bg-white border border-gray-400 hover:bg-gray-50 focus:ring-gray-500`;
      default: // primary
        return `${baseStyles} text-white bg-black border border-transparent hover:bg-gray-800 focus:ring-gray-500`;
    }
  };

  const buttonText = "Export CSV";
  const hasData = data && data.length > 0;
  const hasColumns = columns && columns.length > 0;
  const hasServerSideExport = onExport && typeof onExport === 'function';
  const canExport = !disabled && !isExporting && (hasData || hasServerSideExport) && (hasColumns || hasServerSideExport);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleExport}
        disabled={!canExport}
        className={`${getButtonStyles()} ${className} ${iconOnlyOnMobile ? "max-[639px]:!h-8 max-[639px]:!w-8 max-[639px]:!justify-center max-[639px]:!px-0 max-[639px]:!py-0" : ""}`}
        title={
          !hasData && !hasServerSideExport ? 'No data available to export' :
          !hasColumns && !hasServerSideExport ? 'No columns defined for export' :
          'Export data as CSV'
        }
      >
        {isExporting && (
          <div className="absolute -translate-y-1/2 -translate-x-1/2 top-1/2 left-1/2">
            <LoaderSpinner width="16" height="16" border="2" colorSpinner="blue"/>
          </div>
        )}
        <ArrowDownTrayIcon className={`h-4 w-4 mr-1.5 sm:mr-2 ${iconOnlyOnMobile ? "max-[639px]:!mr-0" : ""}`} />
        <span className={iconOnlyOnMobile ? `max-[639px]:hidden ${labelClassName}` : labelClassName}>{buttonText}</span>
      </button>

      {/* Export Error Modal/Toast */}
      {exportError && (
        <div className="1">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Export Failed</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{exportError}</p>
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setExportError(null)}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Progress Indicator for large datasets */}
      {isExporting && data.length > 1000 && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-blue-50 border border-blue-200 rounded-md p-3 shadow-lg z-10">
          <div className="flex items-center">
            <div className="mr-2">
              <LoaderSpinner width="20" height="20" border="2" parentHeight="h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Preparing Export</p>
              <p className="text-xs text-blue-700">Processing {data.length} records...</p>
            </div>
          </div>
          <div className="mt-2 w-full bg-blue-200 rounded-full h-1">
            <div className="bg-blue-600 h-1 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Usage example component for documentation
export const CSVExportButtonExample = () => {
  const sampleData = [
    { name: 'Product A', sales: 150, revenue: 1500 },
    { name: 'Product B', sales: 200, revenue: 2000 },
  ];

  const sampleColumns = [
    { key: 'name', label: 'Product Name' },
    { key: 'sales', label: 'Units Sold' },
    { key: 'revenue', label: 'Revenue ($)' },
  ];

  return (
    <CSVExportButton
      data={sampleData}
      columns={sampleColumns}
      filename="sales_report"
      variant="primary"
    />
  );
};

export default CSVExportButton; 